import React, { useEffect, useCallback } from "react";
import LoaderComponent from "../../components/LoaderComponent/LoaderComponent";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { initialise, login, setSession } from "../../data/slices/authSlice";
import { getAllTemplates } from "../../data/slices/templates";
import { new_session, newSession, getSessions } from "../../data/slices/sessions";
import { message } from "antd";
import axiosInstance from "../../utils/axios";
import ltiAxios from "../../utils/ltiAxios";

function LtiAuthentication() {
  const params = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const accessToken = searchParams.get("accessToken");
  const code = searchParams.get("code");
  const cpu = searchParams.get("cpu");
  const gpu = searchParams.get("gpu");
  const ram = searchParams.get("ram");
  const storage = searchParams.get("storage");

  // session=1 means the path segment is an existing Session name (LTI v2,
  // which provisions the desktop server-side) — connect straight to the
  // viewer instead of creating a new session from a template.
  const isExistingSession = searchParams.get("session") === "1";

  const { isAuthenticated, user: authUser } = useSelector((state) => state.auth);
  const [loading, setLoading] = React.useState(true);

  const createSession = useCallback((desktop, userOrgId) => {
    const sessionPayload = {
      namespace: userOrgId || "default",
      template: desktop.name,
      sessionConfig: {
        cpu: cpu ? cpu : desktop?.skuConfig?.cpu?.defaultValue,
        ram: ram ? ram : desktop?.skuConfig?.ram?.defaultValue,
        storage: storage ? storage : desktop?.skuConfig?.storage?.defaultValue,
        // gpu: gpu ? gpu : desktop?.skuConfig?.gpu?.defaultValue ,
        gpu: "0",
        shouldPersist: false
      },
      shouldPersist: false,
      redirectURL: ""
    };

    if (code && code.trim() !== "" && code.trim() !== "%s") {
      sessionPayload.code = code;
    }

    dispatch(
      newSession(sessionPayload)
    )
      .unwrap()
      .then((res) => {
        if (res) {
          dispatch(new_session(res));
          navigate(`/lti/control/${res.name}`, {
            state: res
          });
        }
      })
      .catch((err) => {
        message.error(err.message);
        // navigate("/admin/plans");
      });
  }, [dispatch, navigate, cpu, ram, storage, code]);

  const resolveDesktop = useCallback((orgId) => {
    // LTI v2: the desktop already exists (provisioned server-side). Load the
    // user's sessions first so the viewer's store is populated — otherwise
    // VNCViewer's "session not found" guard fires before the list loads and
    // reports "Desktop has been closed". Then connect to the viewer.
    if (isExistingSession) {
      dispatch(getSessions())
        .unwrap()
        .then((result) => {
          const found = (result?.sessions || []).find(
            (s) => s?.id === params?.desktop_id || s?.name === params?.desktop_id
          );
          navigate(
            `/lti/control/${params?.desktop_id}`,
            found ? { state: found } : undefined
          );
        })
        .catch(() => navigate(`/lti/control/${params?.desktop_id}`));
      return;
    }
    // LTI v1: the path segment is a template name — create the session.
    dispatch(getAllTemplates({ query: "" }))
      .unwrap()
      .then((res) => {
        const targetDesktop = res?.templates?.find(
          (desktop) => desktop?.name === params?.desktop_id
        );
        if (targetDesktop) {
          createSession(targetDesktop, orgId);
        } else {
          message.error("Desktop not found");
          setLoading(false);
        }
      });
  }, [dispatch, navigate, params?.desktop_id, isExistingSession, createSession]);

  useEffect(() => {
    async function fetchUser() {
      try {
        if (accessToken) {
          setSession(accessToken);
          const response = await ltiAxios.get("profile/whoami");
          const user = response.data;
          axiosInstance.defaults.headers["X-User-ID"] = user?.username;
          dispatch(initialise({ isAuthenticated: true, user }));
          resolveDesktop(user?.orgId);
        } else {
          dispatch(initialise({ isAuthenticated: false, user: null }));
          window.localStorage.removeItem("gomydesk_token");
        }
      } catch (error) {
        message.error("Token is invalid or expired");
        dispatch(initialise({ isAuthenticated: false, user: null }));
        window.localStorage.removeItem("gomydesk_token");
      }
    }

    if (isAuthenticated) {
      resolveDesktop(authUser?.orgId);
    } else {
      fetchUser();
    }
  }, [dispatch, accessToken, isAuthenticated, authUser?.orgId, resolveDesktop]);

  return (
    <div className="lti-auth">
      <div className="lti-loading">{loading ? <LoaderComponent /> : "Desktop Not Found"}</div>
    </div>
  );
}

export default LtiAuthentication;
